import { useCallback, useEffect, useState } from "react";
import { Box, Button, Group, NumberInput, Stack, Stepper, Text, Textarea, TextInput } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { showNotification } from "@mantine/notifications";
import { useAuthUser } from "react-auth-kit";
import { useTranslation } from "react-i18next";
import FileList from "../fileList";
import { FormSectionCard } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import {
  ExpenseAmountHkdField,
  ExpenseClaimSummary,
  ExpenseCurrencySelect,
  ExpenseItemsField,
  ExpenseReceiptDropzone,
  ExpenseTypeSelect,
  DepartmentSelect,
  ReceiptPreview,
  expenseTypeId,
} from "./expenseClaimFields";

const claimValues = (values) => {
  const next = { ...values };
  next.expenseType = expenseTypeId(next.expenseType);
  delete next.expenseTypeMode;
  return next;
};

const formatDate = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const ReadRow = ({ label, value }) => (
  <div>
    <Text size="xs" color="dimmed">
      {label}
    </Text>
    <Text size="sm">{value || "—"}</Text>
  </div>
);

const ExpenseClaimWizard = ({ form, formAction }) => {
  const { t } = useTranslation();
  const auth = useAuthUser();
  const [api] = useServerApi();
  const [step, setStep] = useState(0);
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [checked, setChecked] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const values = form?.values || {};
  const entertainment = values.expenseTypeMode === "PER_ATTENDEE";
  const items = Array.isArray(values.items) ? values.items : [];
  const readSomething = Boolean(values.merchant || values.total != null || items.length);
  const onPreview = useCallback((preview) => {
    if (!preview) {
      setChecked(false);
      return;
    }
    setChecked(true);
    setBlockReason(preview.submitBlockReason || "");
  }, []);

  useEffect(() => {
    if (form?.values?.department || form?.values?._id) return undefined;
    const signedIn = auth?.() ?? {};
    const signedInDepartment = signedIn.departmentId?._id || signedIn.departmentId;
    if (signedInDepartment) {
      form.setFieldValue("department", String(signedInDepartment));
      return undefined;
    }
    const id = signedIn.id || signedIn._id;
    if (!id) return undefined;
    let active = true;
    api
      .getById({ apiEntity: "user", id })
      .then((user) => {
        if (!active || form?.values?.department) return;
        const departmentId = user?.departmentId?._id || user?.departmentId;
        if (departmentId) form.setFieldValue("department", String(departmentId));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
    // Fill once when the add form opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveClaim = async (submit) => {
    if (!formAction?.open) return;
    setSaving(true);
    try {
      const result = await api.add({
        apiEntity: "expenseClaim",
        values: claimValues(form.values),
      });
      if (!result?.success) {
        throw new Error(result?.error?.message || result?.message || "Could not save the claim");
      }
      const id = result.data?._id;
      if (submit && id) {
        const submitted = await api.Mca.submitEntity({
          targetEntity: "expenseClaim",
          targetEntityId: id,
        });
        if (submitted && submitted.success === false) {
          throw new Error(submitted?.error?.message || submitted?.message || "Could not submit the claim");
        }
      }
      showNotification({
        color: submit ? "green" : "teal",
        title: submit ? "Submitted for approval" : "Draft saved",
        message: submit
          ? "The claim is with the approvers."
          : "Open the claim to change it or submit it later.",
      });
      formAction.close(false);
      formAction.open({ mode: "edit", id });
    } catch (error) {
      showNotification({
        color: "red",
        title: "Could not save",
        message: error?.message || "Could not save the claim",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={(theme) => ({
        display: "grid",
        gridTemplateColumns: "220px minmax(0, 1fr)",
        columnGap: theme.spacing.xl,
        alignItems: "start",
        marginTop: theme.spacing.xs,
      })}
    >
    <Stepper
      active={step}
      onStepClick={setStep}
      orientation="vertical"
      styles={{ content: { display: "none" } }}
    >
      <Stepper.Step label={t("Upload Receipt")} description={t("Drop or choose a file")} allowStepSelect={!reading} />
      <Stepper.Step label={t("Claim Detail")} description={t("Type, department, and purpose")} allowStepSelect={!reading} />
      <Stepper.Step label={t("Pre-approval")} description={t("Attach the email")} allowStepSelect={!reading} />
      <Stepper.Step label={t("Review")} description={t("Save or submit")} allowStepSelect={!reading} />
    </Stepper>
    <Box sx={{ minWidth: 0 }}>
      {step === 0 ? (
        <>
        <FormSectionCard
          title="Upload Receipt"
          description="Drop or choose a JPG, PNG, or PDF. It is read straight away."
          paperPadding="lg"
        >
          <ExpenseReceiptDropzone form={form} onBusyChange={setReading} />
        </FormSectionCard>
        {readSomething || values.receiptFile ? (
          <Stack spacing="lg">
            <FormSectionCard title="Receipt file" description="Compare this with what was read." paperPadding="md">
              <ReceiptPreview file={values.receiptFile} />
            </FormSectionCard>
            <FormSectionCard title="Read from the receipt" description="Check this before you continue." paperPadding="lg">
              <Stack spacing="sm">
                <ReadRow label="Merchant" value={values.merchant} />
                <ReadRow label="Date" value={formatDate(values.receiptDate)} />
                <ReadRow label="Currency" value={values.currency} />
                <ReadRow label="Total" value={values.total != null ? Number(values.total).toFixed(2) : ""} />
              </Stack>
                <Stack spacing={6} mt="md">
                  <Text size="xs" color="dimmed">
                    Line items
                  </Text>
                  {items.length ? (
                    items.map((item, index) => (
                      <Text size="sm" key={`${item.name || "line"}-${index}`}>
                        {item.name || "Item"}
                        {item.quantity != null && item.quantity !== "" ? ` × ${item.quantity}` : ""}
                        {item.amount != null && item.amount !== "" ? ` · ${Number(item.amount).toFixed(2)}` : ""}
                      </Text>
                    ))
                  ) : (
                    <Text size="sm">No line items were read.</Text>
                  )}
              </Stack>
            </FormSectionCard>
          </Stack>
        ) : null}
        </>
      ) : null}
      {step === 1 ? (
        <>
        <FormSectionCard
          title="Claim Detail"
          description="Choose the type and say what the expense was for."
          paperPadding="lg"
        >
          <Stack>
            <ExpenseTypeSelect form={form} value={values.expenseType} onChange={(next) => form.setFieldValue("expenseType", next)} />
            <DepartmentSelect
              form={form}
              value={values.department}
              onChange={(next) => form.setFieldValue("department", next)}
            />
            {entertainment ? (
              <NumberInput
                label="Attendees"
                description="Whole number of people at the entertainment"
                min={1}
                step={1}
                precision={0}
                value={values.attendeeCount ?? ""}
                onChange={(next) => form.setFieldValue("attendeeCount", next === "" ? null : Math.round(Number(next)))}
              />
            ) : null}
            <Textarea label="Purpose" minRows={2} value={values.purpose || ""} onChange={(event) => form.setFieldValue("purpose", event.currentTarget.value)} />
          </Stack>
        </FormSectionCard>
        <FormSectionCard
          title="Receipt figures"
          description="These are the amounts printed on the receipt."
          paperPadding="lg"
        >
          <Stack>
            <TextInput label="Merchant" value={values.merchant || ""} onChange={(event) => form.setFieldValue("merchant", event.currentTarget.value)} />
            <DatePicker label="Receipt date" value={values.receiptDate ? new Date(values.receiptDate) : null} onChange={(next) => form.setFieldValue("receiptDate", next)} />
            <ExpenseCurrencySelect value={values.currency} onChange={(next) => form.setFieldValue("currency", next)} />
            <NumberInput label="Total" precision={2} hideControls value={values.total ?? ""} onChange={(next) => form.setFieldValue("total", next)} />
            <NumberInput label="Subtotal" precision={2} hideControls value={values.subtotal ?? ""} onChange={(next) => form.setFieldValue("subtotal", next)} />
            <NumberInput label="Tax" precision={2} hideControls value={values.tax ?? ""} onChange={(next) => form.setFieldValue("tax", next)} />
          </Stack>
        </FormSectionCard>
        <FormSectionCard
          title="Hong Kong amount"
          description="Approval uses the amount in HKD, not the foreign total."
          paperPadding="lg"
        >
          <Stack>
            <ExpenseAmountHkdField form={form} value={values.amountHkd} onChange={(next) => form.setFieldValue("amountHkd", next)} />
            <ExpenseItemsField form={form} value={values.items} />
          </Stack>
        </FormSectionCard>
        </>
      ) : null}
      {step === 2 ? (
        <>
        <FormSectionCard
          title="Approval email"
          description="Attach the email that approved this expense. This is separate from the receipt."
          paperPadding="lg"
        >
          <FileList
            form={form}
            name="preApprovalFiles"
            uploadLabel="Upload email"
            tableMarginBottom={0}
            showNoFilesMessage
          />
        </FormSectionCard>
        <FormSectionCard title="Remark" description="Optional." paperPadding="lg">
          <Textarea
            label="Remark"
            minRows={3}
            value={values.remark || ""}
            onChange={(event) => form.setFieldValue("remark", event.currentTarget.value)}
          />
        </FormSectionCard>
        </>
      ) : null}
      {step === 3 ? (
        <>
        <FormSectionCard
          title="This claim"
          description="Check the claimant, the amount in HKD, and who has to approve it."
          paperPadding="lg"
        >
          <ExpenseClaimSummary form={form} onPreview={onPreview} />
        </FormSectionCard>
        <Group spacing="sm">
          <Button loading={saving} variant="default" onClick={() => saveClaim(false)}>
            Save draft
          </Button>
          <Button loading={saving} disabled={!checked || Boolean(blockReason)} onClick={() => saveClaim(true)}>
            Submit for approval
          </Button>
        </Group>
        </>
      ) : null}
      <Group position="right" mt="md">
        {step > 0 ? (
          <Button variant="default" onClick={() => setStep((current) => current - 1)}>
            Back
          </Button>
        ) : null}
        {step < 3 ? (
          <Button disabled={reading} onClick={() => setStep((current) => current + 1)}>
            Next
          </Button>
        ) : null}
      </Group>
    </Box>
    </Box>
  );
};

export default ExpenseClaimWizard;
